// @flow

/*
 * ACCOUNT STORE SAGAS
 *
 * These routines listen for request actions, and satisfies them.  These are asynchronous routines, and upon
 * completion will dispatch a new action with the results
 *
 *
 */
import { call, delay, put, select, take, takeEvery } from 'redux-saga/effects';
import { Map as ImmutableMap } from 'immutable';

import AxiosFactory from 'utils/axiosFactory';
import { getLogger } from 'utils/logger';
import { Config, ENVIRONMENT } from 'utils/webappConfig';

import { getInstitutionLogins } from 'data/institutionLogins/actions';
import {
  getCredentialBlobsForRefreshingLogins,
  getCredentialsForRefreshingLogins,
  getIdsRefreshingWithUserActionsPending,
  getInstitutionLoginsById,
  getInstitutionLoginForId,
  hasUserActionBeenQueued,
  isUserActionPending,
  wasUserActionCancelled,
} from 'data/institutionLogins/selectors';
import { transformRefreshingLoginsToRequestData, transformMfaResponseToMfaChallenge } from 'data/institutionLogins/transformers';
import { getTransactions } from 'data/transactions/actions';

import { createDialog } from 'data/rootUi/actions';
import { DIALOG_TYPE as DIALOG_TYPE_ACCOUNT_DISCOVERY, mkAccountDiscoveryDialogProps }
  from 'components/Dialogs/AccountDiscovery/types';
import { DIALOG_TYPE as REFRESH_TIMEOUT_DIALOG, mkRefreshTimeoutDialogProps, mkConfirmationDialogProps }
  from 'components/Dialogs/RefreshTimeoutDialog';
import { mkRootUiData } from 'data/rootUi/types';

import { getQcsSyncResources, qcsSyncCreateResource, qcsSyncUpdateResource, qcsSyncDeleteResource, qcsSyncUpdateResources }
  from 'data/qcsSyncResourceSagaHelpers';
import { mkQcsSyncResourceConfig } from 'data/qcsSyncResourceTypes';
import { preRefreshAccountsCheck } from 'data/institutionLogins/sagas';
import { RefreshAccountsMfaFormSubmitError } from 'data/institutionLogins/types';


import { getLastSyncDate } from './selectors';
import {
  transformAccountToRequestData,
  transformAccountsToRequestData,
  transformResponseToAccounts,
  transformResponseToRefreshStatus,
  transformResponseToInvestmentAccounts,
  transformInvestmentAccountToQ17RequestData,
} from './transformers';
import * as actions from './actions';


const qcsAxios = AxiosFactory.get('qcs');
const log = getLogger('data/accounts/sagas.js');

// poll every 3 seconds up to a total of 3 minutes and 30 seconds (seconds are buffer over QCS timeouts)
const REFRESH_POLL_INTERVAL = 3;
const REFRESH_POLL_ATTEMPTS = 210 / REFRESH_POLL_INTERVAL;

// ===============================================================================================
// SAGAS functions for accounts.  These are the functions responsible for communicating directly
// with external endpoints asynchronously.  Data is returned via ACTIONS, however, these can be
// split out (and should be) to be fully functional and JS shareable API callers via Axios and
// simply return an object in a provided callback, which can then choose to call the actions
//

// =================================================================================================
// Refresh Accounts
// =================================================================================================

function* isRefreshCancelledForLogin(login) {
  return yield select(wasUserActionCancelled, login.id);
}

export function* refreshAccounts(action: any): Generator<*, *, *> {
  const { payload: data, meta } = action;
  const institutionLoginId = data != null ? data.institutionLoginId : null;

  const loginsToRefresh = [];
  if (institutionLoginId) {
    const login = yield select(getInstitutionLoginForId, data.institutionLoginId);
    if (login) {
      const refreshCancelledForLogin = yield* isRefreshCancelledForLogin(login);
      if (!refreshCancelledForLogin) {
        loginsToRefresh.push(login);
      }
    }
  } else {
    const loginsById = yield select(getInstitutionLoginsById);

    const allLogins = loginsById.valueSeq().toArray();
    for (let i = 0; i < allLogins.length; ++i) {
      const login = allLogins[i];
      const refreshCancelledForLogin = yield* isRefreshCancelledForLogin(login);
      if (!refreshCancelledForLogin) {
        loginsToRefresh.push(login);
      }
    }
  }

  if (loginsToRefresh.length === 0) {
    yield put(actions.refreshAccountsCompleted({ institutionLoginId, status: 'NO_ACCOUNTS_TO_REFRESH' }, meta));
    return;
  }

  const precheckPassed = yield* preRefreshAccountsCheck(loginsToRefresh, action);
  if (!precheckPassed) {
    yield put(actions.refreshAccountsPrecheck());
    return;
  }

  // special case when refreshing a single institution login
  let requestUrl;
  if (loginsToRefresh.length === 1) {
    requestUrl = `${Config.environments[ENVIRONMENT].services_url}/institution-logins/${loginsToRefresh[0].id}/refresh`;
  } else {
    requestUrl = `${Config.environments[ENVIRONMENT].services_url}/institution-logins/refresh`;
  }

  const credentialsBlobsForRefreshingLogins = yield select(getCredentialBlobsForRefreshingLogins);
  const credentialsForRefreshingLogins = yield select(getCredentialsForRefreshingLogins);
  const requestBody = transformRefreshingLoginsToRequestData(
    loginsToRefresh,
    credentialsBlobsForRefreshingLogins,
    credentialsForRefreshingLogins
  );

  let pollingReference = '';

  try {
    const response = yield call(qcsAxios.post, requestUrl, requestBody);
    log.log('Refresh accounts response is', response.data);

    pollingReference = response.data.pollingReference;
    log.log('pollingReference=', pollingReference);
  } catch (error) {
    log.log('Error refreshing accounts', error);
    yield put(actions.refreshAccountsCompleted({ institutionLoginId, status: 'FAILED_WHEN_POLLING' }, meta));

    // if we failed to kickoff refresh, were done
    return;
  }

  // poll until refresh is ready or cancel action
  if (pollingReference && pollingReference.length && pollingReference.length > 0) {
    requestUrl = `${Config.environments[ENVIRONMENT].services_url}${pollingReference}`;
    yield* refreshPoll(requestUrl, institutionLoginId, meta);
  } else {
    yield put(actions.refreshAccountsCompleted({ institutionLoginId, status: 'DONE_PROCESSING' }, meta));
  }
}

export function* submitMfaResponse({ payload : mfaResponse, meta }: {payload: any, meta: any}): Generator<*, *, *> {
  const { resolve, reject } = meta;

  // TODO: If user submits an MFA response but we are no longer polling, perhaps we should just fail...

  const requestUrl = `${Config.environments[ENVIRONMENT].services_url}/${mfaResponse.submitUrl}`;
  const requestBody = {
    institutionLoginId: mfaResponse.institutionLoginId,
    mfaResponses: mfaResponse.answers.map((answer) => ({
      key: answer.id,
      response: answer.value,
    })),
  };

  try {
    yield call(qcsAxios.post, requestUrl, requestBody);

    yield put(actions.refreshAccountsMfaFormSubmitResponse(mfaResponse.institutionLoginId));
    yield call(resolve, mfaResponse.institutionLoginId);

  } catch (error) {

    log.log('Received error while submitting MFA', mfaResponse.institutionLoginId, error);
    const errorResponse = new RefreshAccountsMfaFormSubmitError(mfaResponse.institutionLoginId, error);

    yield put(actions.refreshAccountsMfaFormSubmitResponse(errorResponse));
    yield call(reject, errorResponse);
  }
}

function* refreshPoll(url, institutionLoginId, meta): Generator<*, *, *> {
  assert(url, 'polling url is invalid - that should never happen...');
  let response;
  let numTimesLeftToPoll = REFRESH_POLL_ATTEMPTS;
  while (numTimesLeftToPoll > 0) {
    try {
      response = yield call(qcsAxios.get, url);
      log.log('Polling for refresh status', response.data);

      const refreshStatus = transformResponseToRefreshStatus(response.data);
      log.log('Status after transforming...', refreshStatus);

      // send refresh status
      yield put(actions.refreshAccountsStatuses(refreshStatus));

      if (refreshStatus.status === 'ACTION_REQUIRED') {
        const { institutionLogins } = refreshStatus;
        for (let i = 0; i < institutionLogins.size; ++i) {
          const login = institutionLogins.get(i);
          if (login && login.status === 'ACTION_REQUIRED') {
            const userActionAlreadyQueued = yield select(hasUserActionBeenQueued, login.id);
            log.log('Refresh login status has action required', login, userActionAlreadyQueued);

            if (!userActionAlreadyQueued && login.aggregators && login.aggregators.size > 0) {

              // todo: currently QCS only supports a single aggregator per login, will need to
              // todo: fix when QCS supports multiple aggregators per login
              const aggregator = login.aggregators.get(0);

              // todo: this should be checking aggregator.aggStatus === 'MFA_CHALLENGE_READY_FOR_USER' but QCS isn't setting this
              if (aggregator && aggregator.mfaChallenges) {
                const mfaChallenge = transformMfaResponseToMfaChallenge(
                  login.id, aggregator.mfaChallenges, login.submitUrl ? login.submitUrl : ''
                );

                const institutionLogin = yield select(getInstitutionLoginForId, mfaChallenge.institutionLoginId);
                const mfaChallengeDialogData = mkRootUiData({
                  id: login.id,
                  type: DIALOG_TYPE_ACCOUNT_DISCOVERY,
                  allowNesting: false,

                  props: ImmutableMap(mkAccountDiscoveryDialogProps({
                    mode: 'PROVIDE-MFA',
                    institutionLogin,
                    mfaChallenge,
                  })),
                });

                log.log('Triggering MFA', mfaChallenge);
                yield put(createDialog(mfaChallengeDialogData));
                yield put(actions.refreshAccountsTriggeredMfa(mfaChallenge.institutionLoginId));
              }
            }
          }
        }
      }

      const userActionIsPending = yield select(isUserActionPending);
      if (!userActionIsPending && !response.data.isProcessing) {
        yield put(actions.refreshAccountsCompleted({ institutionLoginId, status: 'DONE_PROCESSING' }, meta ));

        // server is done processing and we are not waiting for an MFA so stop polling
        // todo: can maybe optimize by not polling while waiting for MFA but need to restart once MFA is submitted
        break;
      }

      numTimesLeftToPoll -= 1;
      log.log(`Number of times left to poll is ${numTimesLeftToPoll}`);
      yield delay(REFRESH_POLL_INTERVAL * 1000);
    } catch (error) {
      log.log('How did we get here...', error);
      yield put(actions.refreshAccountsCompleted({ institutionLoginId, status: 'FAILED_WHEN_POLLING' }, meta));
      break;
    }
  }

  if (numTimesLeftToPoll === 0) {
    const idsRefreshingWithUserActionsPending = yield select(getIdsRefreshingWithUserActionsPending);
    if (idsRefreshingWithUserActionsPending.size > 0) {
      yield put(createDialog(mkRootUiData({
        type: REFRESH_TIMEOUT_DIALOG,
        props: ImmutableMap(mkRefreshTimeoutDialogProps({
          dialogIdsToClose: idsRefreshingWithUserActionsPending.toList(),
          confirmationDialogProps: mkConfirmationDialogProps({
            content: 'The server terminated the refresh operation due to length of time.',
            confirmLabel: 'retry',
            denyLabel: 'cancel',
            confirmAction: actions.refreshAccounts(),
          }),
        })),
      })));
    }
    yield put(actions.refreshAccountsCompleted({ institutionLoginId, status: 'TIMEOUT_WHILE_PROCESSING' }, meta));
  }

  // whether we succeeded or not, try and get any new institution-logins/accounts/transactions on server
  yield put(getInstitutionLogins());
  yield put(actions.getAccounts());
  yield put(getTransactions(undefined, { context: 'refresh' }));
}

/*
export function* cleanUpDeletedAccount (action): Generator<*, *, *> {

  console.log("CLEAN UP ACCOUNT ", action);
  // if this is a goal account, we need to delete all the transfers on the other side

  const accountsById = yield select(getAccountsByIdIncludingGoals);
  console.log("Accounts are ", accountsById)

  let account;
  if (action.type === 'DELETE_GOAL_SUCCESS') {
    account = accountsById.get(action.payload.accountId);
    console.log("GOAL ACCOUNT IS ", account);
  } else {
    account = action.payload.account.isDeleted ? action.payload.account : null;
  }

  if (account?.type === 'GOAL') {
    console.log("Processing GOAL account deletion...", account);
    const txnsByAccountId = yield select(getTransactionsByAccountId);
    const txns = txnsByAccountId.get(account.id);
    if (txns) {
      let txnsToDelete = new ImmutableList();
      txns.forEach((txn) => {
        if (isTransferTxn(txn) && !isSplitTxn(txn)) {
          const match = findMatchingTransferTx(txn, txnsByAccountId.get(txn.coa.id));
          if (match) txnsToDelete = txnsToDelete.push(match);
        }
      })
      console.log("TRANSACTIONS TO DELETE ARE ", txnsToDelete.toJS());
    }
  }

  // then we need to simply delete the transactions out of Redux
  // by calling the special action to do that

}
*/

// =================================================================================================
// Account Watchers
// =================================================================================================

const mkResourceConfig = (successAction, failureAction) => mkQcsSyncResourceConfig({
  resourceName: 'account',
  resourceBaseUrl: '/accounts',
  getLastSyncDate,

  transformResponseToResources: transformResponseToAccounts,
  transformResourceToRequestData: transformAccountToRequestData,
  transformResourcesToRequestData: transformAccountsToRequestData,

  successAction,
  failureAction,
});



export function* getAccountsActionWatcher(): Generator<*, *, *> {
  // using a vanilla saga pattern so we only process a single get request
  //   - takeEvery would process every request
  //   - takeLatest would cancel any current request and start request again
  while (true) {
    const action = yield take(actions.getAccounts);

    const config = mkResourceConfig(actions.getAccountsSuccess, actions.getAccountsFailure);
    yield call(getQcsSyncResources, config, action);
  }
}

export function* createAccountActionWatcher(): Generator<*, *, *> {
  const config = mkResourceConfig(actions.createAccountSuccess, actions.createAccountFailure);
  yield takeEvery(actions.createAccount, qcsSyncCreateResource, config);
}

export function* updateAccountActionWatcher(): Generator<*, *, *> {
  const config = mkResourceConfig(actions.updateAccountSuccess, actions.updateAccountFailure);
  yield takeEvery(actions.updateAccount, qcsSyncUpdateResource, config);
}

export function* upsertAccountsActionWatcher(): Generator<*, *, *> {
  const config = mkResourceConfig(actions.upsertAccountsSuccess, actions.upsertAccountsFailure);
  yield takeEvery(actions.upsertAccounts, qcsSyncUpdateResources, config);
}

export function* deleteAccountActionWatcher(): Generator<*, *, *> {
  const config = mkResourceConfig(actions.deleteAccountSuccess, actions.deleteAccountFailure);
  yield takeEvery(actions.deleteAccount, qcsSyncDeleteResource, config);
}

export function* refreshAccountsWatcher(): Generator<*, *, *> {
  // using a vanilla saga pattern so we only process a single get request
  //   - takeEvery would process every request
  //   - takeLatest would cancel any current request and start request again
  while (true) {
    const action = yield take(actions.refreshAccounts);
    yield call(refreshAccounts, action);
  }
}

export function* refreshAccountsMfaFormSubmitActionWatcher(): Generator<*, *, *> {
  yield takeEvery(actions.refreshAccountsMfaFormSubmit, submitMfaResponse);
}


// =================================================================================================
// Investment Account Watchers
// =================================================================================================

const mkResourceConfigInvestmentAccounts = (successAction, failureAction) => mkQcsSyncResourceConfig({
  resourceName: 'investment-account',
  resourceBaseUrl: '/q17/accounts',
  getLastSyncDate: () => null,      // We always want to refresh

  transformResponseToResources: transformResponseToInvestmentAccounts,
  transformResourceToRequestData: transformInvestmentAccountToQ17RequestData,

  successAction,
  failureAction,
});

export function* getInvestmentAccountsActionWatcher(): Generator<*, *, *> {
  // using a vanilla saga pattern so we only process a single get request
  //   - takeEvery would process every request
  //   - takeLatest would cancel any current request and start request again
  while (true) {
    const action = yield take(actions.getInvestmentAccounts);

    const config = mkResourceConfigInvestmentAccounts(
      actions.getInvestmentAccountsSuccess, actions.getInvestmentAccountsFailure
    );
    yield call(getQcsSyncResources, config, action);
  }
}

export function* createInvestmentAccountActionWatcher(): Generator<*, *, *> {
  const config = mkResourceConfigInvestmentAccounts(
    actions.createInvestmentAccountSuccess, actions.createInvestmentAccountFailure
  );
  yield takeEvery(actions.createInvestmentAccount, qcsSyncCreateResource, config);
}

export function* updateInvestmentAccountActionWatcher(): Generator<*, *, *> {
  const config = mkResourceConfigInvestmentAccounts(
    actions.updateInvestmentAccountSuccess, actions.updateInvestmentAccountFailure
  );
  yield takeEvery(actions.updateInvestmentAccount, qcsSyncUpdateResource, config);
}

export function* upsertInvestmentAccountsActionWatcher(): Generator<*, *, *> {
  const config = mkResourceConfigInvestmentAccounts(
    actions.upsertInvestmentAccountsSuccess, actions.upsertInvestmentAccountsFailure
  );
  yield takeEvery(actions.upsertInvestmentAccounts, qcsSyncUpdateResources, config);
}

export function* deleteInvestmentAccountActionWatcher(): Generator<*, *, *> {
  const config = mkResourceConfigInvestmentAccounts(
    actions.deleteInvestmentAccountSuccess, actions.deleteInvestmentAccountFailure
  );
  yield takeEvery(actions.deleteInvestmentAccount, qcsSyncDeleteResource, config);
}

// =================================================================================================
// Exports
// =================================================================================================

export default [
  getAccountsActionWatcher,
  createAccountActionWatcher,
  updateAccountActionWatcher,
  deleteAccountActionWatcher,
  upsertAccountsActionWatcher,
  refreshAccountsWatcher,
  refreshAccountsMfaFormSubmitActionWatcher,
  getInvestmentAccountsActionWatcher,
  createInvestmentAccountActionWatcher,
  updateInvestmentAccountActionWatcher,
  upsertInvestmentAccountsActionWatcher,
  deleteInvestmentAccountActionWatcher,
];
